import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { api, type AppNotification } from "../lib/api";
import { cn } from "../lib/cn";

type Filter = "all" | "unread" | "read";

function SeverityIcon({ severity, className }: Readonly<{ severity: AppNotification["severity"]; className?: string }>) {
  if (severity === "error")
    return (
      <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
        <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8 1.5ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm7.25-3.5a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4Zm.75 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />
      </svg>
    );
  if (severity === "warning")
    return (
      <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
        <path fillRule="evenodd" d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm-.97 4.496a.75.75 0 0 1 1.5 0v2.5a.75.75 0 0 1-1.5 0v-2.5Zm.75 5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />
      </svg>
    );
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8 1.5ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm11.78-1.72a.75.75 0 0 0-1.06-1.06L6.75 9.19 5.28 7.72a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0l4.5-4.5Z" />
    </svg>
  );
}

const SEVERITY_BAR: Record<AppNotification["severity"], string> = {
  error: "bg-danger-500",
  warning: "bg-warning-500",
  success: "bg-success-500",
};

const SEVERITY_ICON: Record<AppNotification["severity"], string> = {
  error: "text-danger-500",
  warning: "text-warning-500",
  success: "text-success-500",
};

const SEVERITY_BADGE: Record<AppNotification["severity"], string> = {
  error: "bg-danger-50 text-danger-700 border-danger-200",
  warning: "bg-warning-50 text-warning-700 border-warning-200",
  success: "bg-success-50 text-success-700 border-success-200",
};

const TYPE_LABELS: Record<AppNotification["type"], string> = {
  crawl_failed: "Crawl Failed",
  crawl_succeeded: "Crawl Success",
  crawl_warning: "Crawl Warning",
  schedule_failed: "Schedule Failed",
  schedule_succeeded: "Schedule Success",
  schedule_warning: "Schedule Warning",
  ingest_failed: "Ingest Failed",
};

function typeLabel(type: AppNotification["type"]) {
  return TYPE_LABELS[type] ?? type;
}

function tabBadgeClass(active: Filter, tab: Filter): string {
  if (active !== tab) return "bg-ash/15 text-ash";
  if (tab === "unread") return "bg-danger-500 text-white";
  return "bg-ink/90 text-white";
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const EMPTY_MSG: Record<Filter, string> = {
  unread: "You're all caught up - no unread notifications.",
  read: "No notifications have been read yet.",
  all: "No crawl or schedule activity to report.",
};

function EmptyState({ filter }: Readonly<{ filter: Filter }>) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-rule bg-surface shadow-sm">
        <svg className="h-7 w-7 text-ash/40" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M18 16v-5a6 6 0 0 0-5-5.92V4a1 1 0 0 0-2 0v1.08A6 6 0 0 0 6 11v5l-2 2v1h16v-1l-2-2Z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
          />
          <path d="M10 20a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-[15px] font-semibold text-ink">All clear</p>
      <p className="mt-1.5 max-w-[260px] text-[13px] leading-relaxed text-ash">{EMPTY_MSG[filter]}</p>
    </div>
  );
}

export function NotificationsPage() {
  const [, setLocation] = useLocation();
  const [filter, setFilter] = useState<Filter>("all");
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<AppNotification[]>("/api/v1/notifications"),
    refetchInterval: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/api/v1/notifications/${encodeURIComponent(id)}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch("/api/v1/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/notifications/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const all = query.data ?? [];
  const unreadCount = all.filter((n) => !n.readAt).length;
  const readCount = all.filter((n) => !!n.readAt).length;
  const errorCount = all.filter((n) => n.severity === "error").length;

  const filtered = all.filter((n) => {
    if (filter === "unread") return !n.readAt;
    if (filter === "read") return !!n.readAt;
    return true;
  });

  const TABS: { f: Filter; count: number }[] = [
    { f: "all", count: all.length },
    { f: "unread", count: unreadCount },
    { f: "read", count: readCount },
  ];

  return (
    <div className="page-container">
      <header className="pt-8 pb-6">
        <div className="eyebrow mb-1">Alerts</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
              Notifications
            </h1>
            <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-ash">
              Crawl and schedule activity. Dismissed entries are permanently removed.
            </p>
          </div>

          {all.length > 0 && (
            <div className="flex shrink-0 items-center gap-2">
              {unreadCount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-danger-500 font-mono text-[10px] font-bold tabular-nums text-white">
                    {unreadCount}
                  </span>
                  <span className="text-[12px] font-medium text-danger-700">Unread</span>
                </div>
              )}
              {errorCount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-danger-500 font-mono text-[10px] font-bold tabular-nums text-white">
                    {errorCount}
                  </span>
                  <span className="text-[12px] font-medium text-danger-700">
                    {errorCount === 1 ? "Error" : "Errors"}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Toolbar */}
      <div className="mb-6 flex items-center justify-between gap-4">
        {/* Segmented filter */}
        <div className="flex gap-1 rounded-xl border border-rule bg-rule/20 p-1">
          {TABS.map(({ f, count }) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                "flex items-center gap-2 rounded-[9px] px-4 py-1.5 text-[13px] font-medium capitalize transition-all duration-150",
                filter === f
                  ? "bg-surface text-ink shadow-sm ring-1 ring-black/5"
                  : "text-ash hover:text-ink",
              )}
            >
              {f}
              {count > 0 && (
                <span
                  className={cn(
                    "min-w-[18px] rounded-full px-1 text-center font-mono text-[10px] font-semibold leading-[18px] tabular-nums transition-colors",
                    tabBadgeClass(filter, f),
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-rule bg-surface px-3 py-1.5 text-[12.5px] font-medium text-ash transition-colors hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M2 8.5 6 12.5 14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Mark all read
          </button>
        )}
      </div>

      {/* Skeleton */}
      {query.isLoading && (
        <div className="space-y-2.5">
          {["s1", "s2", "s3", "s4"].map((k) => (
            <div key={k} className="h-[76px] animate-pulseSoft rounded-xl border border-rule bg-surface" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!query.isLoading && filtered.length === 0 && <EmptyState filter={filter} />}

      {/* List */}
      {filtered.length > 0 && (
        <ul className="space-y-2.5">
          {filtered.map((n) => {
            const isUnread = !n.readAt;
            return (
              <li
                key={n.id}
                className={cn(
                  "group flex overflow-hidden rounded-xl border transition-all duration-150 hover:shadow-md",
                  isUnread ? "border-rule bg-surface shadow-sm" : "border-rule/50 bg-surface/50",
                )}
              >
                {/* Severity accent bar */}
                <div className={cn("w-[3px] shrink-0", SEVERITY_BAR[n.severity])} />

                <div className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3.5">
                  {/* Severity icon */}
                  <SeverityIcon
                    severity={n.severity}
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0 transition-opacity",
                      SEVERITY_ICON[n.severity],
                      !isUnread && "opacity-40",
                    )}
                  />

                  {/* Body */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={cn("rounded-md border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider", SEVERITY_BADGE[n.severity])}>
                        {typeLabel(n.type)}
                      </span>
                      {isUnread && (
                        <span className="rounded-md border border-brand-300 bg-brand-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                          New
                        </span>
                      )}
                      <span
                        title={new Date(n.occurredAt).toLocaleString()}
                        className="font-mono text-[11px] text-ash/60"
                      >
                        {relativeTime(n.occurredAt)}
                      </span>
                    </div>

                    <p className={cn("mt-1 text-[13.5px] font-semibold leading-snug", isUnread ? "text-ink" : "text-ash")}>
                      {n.message}
                    </p>

                    {n.detail && (
                      <p className="mt-0.5 line-clamp-1 font-mono text-[11px] text-ash/70">{n.detail}</p>
                    )}

                    {/* Source chip + actions */}
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-1 gap-y-1">
                      <button
                        type="button"
                        onClick={() => setLocation(`/sources/${encodeURIComponent(n.sourceId)}`)}
                        className="flex items-center gap-1 rounded-md border border-rule bg-surface px-2 py-0.5 font-mono text-[11px] text-ash transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                      >
                        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" aria-hidden>
                          <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                          <path d="M5 7h6M5 10h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                        </svg>
                        {n.sourceName || n.sourceId}
                      </button>

                      <span className="select-none px-0.5 text-ash/30">·</span>

                      {isUnread && (
                        <>
                          <button
                            type="button"
                            onClick={() => markRead.mutate(n.id)}
                            disabled={markRead.isPending}
                            className="rounded-md border border-rule bg-surface px-2.5 py-0.5 text-[12px] font-medium text-ink transition-all hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Mark read
                          </button>
                          <span className="select-none px-0.5 text-ash/30">·</span>
                        </>
                      )}

                      <button
                        type="button"
                        onClick={() => dismiss.mutate(n.id)}
                        disabled={dismiss.isPending}
                        className="rounded-md border border-transparent px-2.5 py-0.5 text-[12px] font-medium text-ash transition-all hover:border-danger-200 hover:bg-danger-50 hover:text-danger-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
